import { useEffect } from "react";

/**
 * Ergonomia de teclado/scroll no mobile (RF-UX-062/063): acompanha o
 * `visualViewport` e publica no `<html>`:
 *
 * - `--app-vh`: altura visivel real. O shell (`.app-frame`) usa isso para
 *   encolher quando o teclado virtual abre, mantendo o composer visivel.
 * - `.kb-open`: sinaliza teclado aberto (safe-area inferior ja coberta).
 * - rola o campo focado para dentro do viewport visual.
 *
 * Degrada para no-op quando a API nao existe (browsers antigos/desktop).
 */
export function useViewportInsets(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    let raf = 0;

    const apply = () => {
      raf = 0;
      const height = Math.round(vv.height);
      const offset = Math.round(vv.offsetTop);
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--app-vh", `${height}px`);
      root.style.setProperty("--app-vt", `${offset}px`);
      root.classList.toggle("kb-open", inset > 80);
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(apply);
    };
    const onFocusIn = () => {
      schedule();
      window.setTimeout(() => {
        const el = document.activeElement as HTMLElement | null;
        const tag = el?.tagName;
        if (el && (tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable)) {
          el.scrollIntoView({ block: "nearest" });
        }
      }, 300);
    };

    apply();
    vv.addEventListener("resize", schedule);
    vv.addEventListener("scroll", schedule);
    window.addEventListener("focusin", onFocusIn);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      vv.removeEventListener("resize", schedule);
      vv.removeEventListener("scroll", schedule);
      window.removeEventListener("focusin", onFocusIn);
      root.style.removeProperty("--app-vh");
      root.style.removeProperty("--app-vt");
      root.classList.remove("kb-open");
    };
  }, []);
}
